import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
    CheckCircle2, ArrowRight, Undo2, AlertTriangle, Users, Info,
} from 'lucide-react';
import * as YearEndService from '../../../../services/YearEndService';
import DelagarePanel from '../components/DelagarePanel';
import { formatSek } from '../constants';

/**
 * Steg 4 för HANDELS-/KOMMANDITBOLAG — "Årets resultat".
 *
 * Bolaget betalar ingen egen inkomstskatt (IL 5 kap. 1 §): resultatet
 * fördelas på delägarna enligt bolagsavtalet och beskattas hos dem. Steget
 * bokför alltså bara överföringen till delägarnas kapitalkonton — ingen
 * bolagsskatt, inga bokslutsdispositioner.
 */
const HbResultStep = ({ companyId, processId, fiscalYearId, completed, onComplete, onUndo, busy, gateError }) => {
    const [preview, setPreview] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [booking, setBooking] = useState(false);

    const fetchPreview = useCallback(async () => {
        if (!processId) return;
        setLoadError(null);
        try {
            const resp = await YearEndService.getHbResult(companyId, processId);
            setPreview(resp?.data || null);
        } catch (err) {
            setLoadError(err.response?.data?.message || 'Kunde inte hämta årets resultat.');
        }
    }, [companyId, processId]);

    useEffect(() => { fetchPreview(); }, [fetchPreview]);

    const handleBook = async () => {
        setBooking(true);
        try {
            await YearEndService.bookHbResult(companyId, processId);
            toast.success('Årets resultat är fördelat och bokfört.');
            await fetchPreview();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte bokföra årets resultat.');
        } finally {
            setBooking(false);
        }
    };

    const problems = preview?.problems || [];
    const canBook = Boolean(preview && !preview.booked && problems.length === 0);

    return (
        <>
            <div className="modern-card-header ye-step-header">
                <div>
                    <h4 className="section-title">
                        Årets resultat
                        {preview?.fiscalYear?.year ? ` — räkenskapsåret ${preview.fiscalYear.year}` : ''}
                    </h4>
                    <p className="section-subtitle">
                        Ett handelsbolag betalar ingen egen inkomstskatt — resultatet fördelas på
                        delägarna och beskattas hos dem. Här bokförs fördelningen och
                        räkenskapsåret låses.
                    </p>
                </div>
            </div>

            <div className="modern-card-body">
            <DelagarePanel companyId={companyId} fiscalYearId={fiscalYearId} onChanged={fetchPreview} />

            <div className="ye-ps-section">
                <h5 className="ye-section-heading">Årets resultat</h5>
                <p className="text-muted" style={{ maxWidth: '68ch' }}>{preview?.explanation}</p>
                {preview && (
                    <div className="ye-tax-figures">
                        <div className="ye-tax-figure">
                            <div className="ye-tax-figure-label">Årets resultat</div>
                            <div className="ye-tax-figure-value">{formatSek(preview.aretsResultat)}</div>
                        </div>
                        <div className="ye-tax-figure">
                            <div className="ye-tax-figure-label">Delägarnas andelar</div>
                            <div className="ye-tax-figure-value">
                                {Math.round((preview.andelssumma || 0) * 1000) / 10} %
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {loadError && (
                <div className="alert alert-warning d-flex align-items-center gap-2">
                    <AlertTriangle size={18} /><span>{loadError}</span>
                </div>
            )}

            {problems.length > 0 && (
                <div className="ye-ps-section">
                    <h5 className="ye-section-heading">
                        <AlertTriangle size={16} style={{ marginRight: 6 }} />
                        Åtgärda innan resultatet kan bokföras
                    </h5>
                    <ul className="mb-0" style={{ maxWidth: '68ch' }}>
                        {problems.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                </div>
            )}

            {preview?.fordelning?.length > 0 && (
                <div className="ye-ps-section">
                    <h5 className="ye-section-heading">
                        <Users size={16} style={{ marginRight: 6 }} />
                        Fördelning på delägarna
                    </h5>
                    <div className="ye-tax-rows">
                        {preview.fordelning.map((d) => (
                            <div className="ye-tax-row" key={d.delagareId}>
                                <span>
                                    {d.namn}
                                    <span className="text-muted" style={{ marginLeft: 8, fontSize: '0.8125rem' }}>
                                        {Math.round((Number(d.resultatandel) || 0) * 1000) / 10} % · konto {d.konto}
                                    </span>
                                </span>
                                <span className="ye-num">{formatSek(d.andel)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="ye-well" style={{ marginTop: '0.75rem' }}>
                        <Info size={14} style={{ marginRight: 6 }} />
                        Fördelningen följer bolagsavtalet — den behöver inte motsvara kapitalandelen
                        och inte vara lika delar. Beloppen förs till delägarnas egna kapitalkonton
                        och blir också delägarnas andel i bilagan INK4DU.
                    </div>
                    {!preview.booked && (
                        <div className="ye-proposal-actions">
                            <button
                                className="btn btn-primary d-inline-flex align-items-center gap-2"
                                onClick={handleBook}
                                disabled={!canBook || booking}
                            >
                                {booking ? 'Bokför…' : 'Bokför årets resultat'}
                            </button>
                        </div>
                    )}
                    {preview.booked && (
                        <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.8125rem' }}>
                            Resultatet är bokfört.
                        </p>
                    )}
                </div>
            )}

            {gateError && (
                <div className="alert alert-danger d-flex align-items-center gap-2">
                    <AlertTriangle size={18} /><span>{gateError.message}</span>
                </div>
            )}
            </div>

            <div className="ye-step-footer">
                {completed ? (
                    <>
                        <span className="ye-footer-done">
                            <CheckCircle2 size={18} style={{ color: 'var(--bs-success)' }} />
                            Årets resultat är fördelat och året låst
                        </span>
                        <button
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                            onClick={onUndo}
                            disabled={busy}
                        >
                            <Undo2 size={14} />
                            Ångra markeringen
                        </button>
                    </>
                ) : (
                    <>
                        <span className="ye-footer-hint">
                            {preview?.booked
                                ? 'Resultatet är bokfört — markera steget som klart för att låsa året.'
                                : 'Bokför årets resultat först.'}
                        </span>
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={onComplete}
                            disabled={busy || !preview?.booked}
                        >
                            {busy ? 'Sparar…' : 'Markera som klart'}
                            <ArrowRight size={16} />
                        </button>
                    </>
                )}
            </div>
        </>
    );
};

export default HbResultStep;
